import React, { createContext, useState } from "react";
import { Snackbar, Typography, useTheme } from "@mui/material";
import Alert from "@/components/alerts/Alert";

type UpdateFeedBackType = {
  open: boolean;
  type: "error" | "default" | "success" | "warning";
  message?: string | React.ReactNode;
  /** How long it shows, 3 seconds unless set; `null` keeps it until closed. */
  duration?: number | null;
  placement?: "bottom" | "top";
};

interface ISnackbar {
  feedback: UpdateFeedBackType;
  setFeedback: React.Dispatch<React.SetStateAction<UpdateFeedBackType>>;
}

const DEFAULT_DURATION = 3000;

const SnackbarContextDefaultValue: ISnackbar = {
  feedback: {
    open: false,
    type: "default",
    duration: DEFAULT_DURATION,
    placement: "bottom",
  },
  setFeedback: () => {},
};

export const SnackbarContext = createContext<ISnackbar>(
  SnackbarContextDefaultValue
);

export const SnackbarProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { palette } = useTheme();

  const [feedback, setFeedback] = useState<UpdateFeedBackType>(
    SnackbarContextDefaultValue.feedback
  );

  // Each `setFeedback` replaces the whole feedback, so one that names no
  // duration would otherwise never hide.
  const autoHideDuration =
    feedback.duration === undefined ? DEFAULT_DURATION : feedback.duration;

  const dismissFeedback = () =>
    setFeedback((data) => ({ ...data, open: false }));

  // Having a div around Alert is needed "SnackBar's child needs to be an element that can hold a ref"
  return (
    <SnackbarContext.Provider
      value={{
        feedback,
        setFeedback,
      }}
    >
      {children}

      <Snackbar
        style={{ backgroundColor: palette.grey[50], borderRadius: 4 }}
        open={feedback.open && feedback.type === "default"}
        autoHideDuration={autoHideDuration}
        onClose={dismissFeedback}
        anchorOrigin={{
          vertical: feedback.placement || "bottom",
          horizontal: "center",
        }}
        message={feedback.message ?? "Your settings have been updated."}
        color={palette.text.primary}
      />

      <Snackbar
        style={{ backgroundColor: palette.grey[50], borderRadius: 4 }}
        open={feedback.open && feedback.type !== "default"}
        autoHideDuration={autoHideDuration}
        onClose={dismissFeedback}
        anchorOrigin={{
          vertical: feedback.placement || "bottom",
          horizontal: "center",
        }}
      >
        <div>
          <Alert
            onClose={dismissFeedback}
            severity={feedback.type !== "default" ? feedback.type : "info"}
            sx={{
              width: "100%",
              backgroundColor: palette.grey[50],
              color: palette.text.primary,
            }}
          >
            <Typography variant="body2" color={palette.text.primary}>
              {feedback.message ? (
                <span>{feedback.message}</span>
              ) : (
                <span>Something went wrong, please try again later.</span>
              )}
            </Typography>
          </Alert>
        </div>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
